import { FC, ReactNode, createContext, useContext, useMemo, useState, useEffect } from "react";
import dayjs from 'dayjs';
// Models
import IEpisode from "app/models/Episode";
// Services
import LocalStorageService from "app/services/LocalStorage.service";

type ContextType = {
  sortBy: string;
  setSortBy: (sortBy:string) => void;
  episodes: IEpisode[];
  onCreate: (episode:IEpisode) => void;
  onUpdate: (episode:IEpisode) => void;
  onPatch: (data:Partial<IEpisode>) => void;
  onDelete: (episodeId:number) => void;
  isPageRoute: boolean;
  activeEpisodeId?: number;
  openedFromPreview?: boolean;
};

const Context = createContext<ContextType | undefined>(undefined);

export const usePageEpisodesContext = ():ContextType => {
  const context = useContext(Context);
  if ( !context ) throw new Error('usePageEpisodesContext must be used within a PageEpisodesProvider');
  return context;
}

type PropsType = {
  page?: any;
  episodes: IEpisode[];
  children: ReactNode;
  isPageRoute: boolean;
  activeEpisodeId?: number;
  openedFromPreview?: boolean;
};

const PageEpisodesProvider:FC<PropsType> = ({
  // Props
  page, episodes, children, isPageRoute, activeEpisodeId, openedFromPreview
}) => {
  const isPrimaryStaplePage = page?.staple && page.staple.order === 1;

  const [ stateEpisodes, setStateEpisodes ] = useState<IEpisode[]>([]);
  const [ sortBy, setSortBy ] = useState<string>(isPrimaryStaplePage ? LocalStorageService.getPageEpisodesSorting() : 'date');

  const compareDateTime = (a: IEpisode, b: IEpisode) => {
    const dateTimeA = a.date ? dayjs(`${a.date} ${a.time ?? ''}`) : null;
    const dateTimeB = b.date ? dayjs(`${b.date} ${b.time ?? ''}`) : null;

    const isValidA = dateTimeA?.isValid() ?? false;
    const isValidB = dateTimeB?.isValid() ?? false;

    if ( isValidA && isValidB ) return dateTimeA!.valueOf() - dateTimeB!.valueOf();

    if ( isValidA ) return -1; // A comes before B
    if ( isValidB ) return 1;  // B comes before A

    return 0; // Both invalid or missing
  };

  const sortedEpisodes = useMemo(() => {
    return stateEpisodes.sort((a, b) => {
      const pageA = a.pageNum ?? 0;
      const pageB = b.pageNum ?? 0;

      const idA = a.id ?? 0;
      const idB = b.id ?? 0;

      if ( sortBy === 'pageNum' ){
        if ( pageA !== pageB ) return pageA - pageB;

        const dateDiff = compareDateTime(a, b);
        if ( dateDiff !== 0 ) return dateDiff;

        return idA - idB;
      }

      const dateDiff = compareDateTime(a, b);
      if ( dateDiff !== 0 ) return dateDiff;
      if ( pageA !== pageB ) return pageA - pageB;
      return idA - idB;
    });
  }, [stateEpisodes, sortBy]);

  useEffect(() => {
    setStateEpisodes(episodes);
  }, [episodes]);

  const handleSetSortBy = (sortBy:any) => {
    setSortBy(sortBy);
    LocalStorageService.setPageEpisodesSorting(sortBy);
  }

  const handleCreate = (episode:IEpisode) => {
    setStateEpisodes((prevState) => ([...prevState, episode]));
  }

  const handleUpdate = (episode:IEpisode) => {
    setStateEpisodes((prevState) => prevState.map((ep:IEpisode) => {
      if ( ep.id === episode.id ) return episode;
      return ep;
    }));
  }

  const handlePatch = (data:Partial<IEpisode>) => {
    setStateEpisodes((prevState) => prevState.map((ep:IEpisode) => {
      if ( ep.id === data.id ) return {...ep, ...data};
      return ep;
    }));
  }

  const handleDelete = (episodeId:number) => {
    setStateEpisodes((prevState) => prevState.filter((ep:IEpisode) => ep.id !== episodeId));
  }

  return (
    <Context.Provider value={{
      sortBy,
      setSortBy: handleSetSortBy,
      episodes: sortedEpisodes,
      onCreate: handleCreate,
      onUpdate: handleUpdate,
      onPatch: handlePatch,
      onDelete: handleDelete,
      isPageRoute,
      activeEpisodeId,
      openedFromPreview
    }}>{children}</Context.Provider>
  )
}

export default PageEpisodesProvider;